/**
 * Chat Message Component - OmniMind style
 * User turns sit right in an ink bubble; assistant turns run full width with
 * their reasoning trace, evidence metadata and confidence score.
 */

import React, { useState } from 'react';
import { ChartRenderer, ChartData } from './ChartRenderer';
import { DemoAnswer } from '../../demo/answers';
import { AnswerMarkdown } from './AnswerMarkdown';

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  thinkingSteps?: ThinkingStep[];
  toolsUsed?: string[];
  confidence?: number;
  processingTime?: number;
  timestamp: Date;
  chartData?: ChartData;
}

interface ThinkingStep {
  step: number;
  reasoning: string;
  action: string;
  observation: string;
}

interface ChatMessageProps {
  message: Message;
  /** Worked example shown when the model call is unavailable */
  demo?: DemoAnswer;
  onFollowUp?: (question: string) => void;
}

const STEP_COLORS = ['#2354F0', '#6D5AE6', '#0E9B8E', '#D9910E', '#12B981', '#C2418C'];

function confidenceColor(confidence: number): string {
  if (confidence >= 0.8) return 'var(--green)';
  if (confidence >= 0.6) return 'var(--amber)';
  return 'var(--red)';
}

export const ChatMessage: React.FC<ChatMessageProps> = ({ message, demo, onFollowUp }) => {
  const [showThinking, setShowThinking] = useState(!!demo);
  const [copied, setCopied] = useState(false);

  const isUser = message.role === 'user';
  const steps = demo
    ? demo.steps.map((s, i) => ({
        step: i + 1,
        action: s.label,
        reasoning: s.source,
        observation: '',
        color: s.color,
        tag: s.tag,
        time: s.time,
      }))
    : (message.thinkingSteps || []).map((s, i) => ({
        ...s,
        color: STEP_COLORS[i % STEP_COLORS.length],
        tag: String(i + 1),
        time: '',
      }));
  const hasSteps = steps.length > 0;
  const confidence = demo ? demo.confidence / 100 : message.confidence;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard unavailable */
    }
  };

  if (isUser) {
    return (
      <div className="umsg">
        <div className="b">{message.content}</div>
      </div>
    );
  }

  return (
    <div className="amsg">
      <span className="av2">
        <i />
      </span>
      <div className="body">
        {/* Reasoning trace */}
        {hasSteps && (
          <div className="think">
            <button
              className="h"
              onClick={() => setShowThinking(!showThinking)}
              style={{ width: '100%', cursor: 'pointer' }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path d="M12 3v3M12 18v3M3 12h3M18 12h3" />
                <rect x="7" y="7" width="10" height="10" rx="3" />
              </svg>
              <span>
                How I answered · {steps.length} step{steps.length === 1 ? '' : 's'}
              </span>
              <span className="t">{showThinking ? 'hide' : 'show'}</span>
            </button>

            {showThinking &&
              steps.map((step, i) => (
                <div className="st" key={i}>
                  <span className="ico" style={{ background: step.color }}>
                    {step.tag}
                  </span>
                  <span>
                    {step.action}
                    {step.reasoning && <span className="dim"> · {step.reasoning}</span>}
                    {step.observation && (
                      <span
                        className="dim"
                        style={{ display: 'block', marginTop: 2, fontSize: 11.5 }}
                      >
                        {step.observation}
                      </span>
                    )}
                  </span>
                  {step.time && <span className="t">{step.time}</span>}
                </div>
              ))}
          </div>
        )}

        {/* Answer */}
        {demo ? (
          <>
            <div style={{ marginBottom: 8 }}>
              <span className="tg tg-p" title="Worked example - the model call needs an API key">
                Demo answer
              </span>
            </div>
            <div className="demo-answer">{demo.body}</div>
          </>
        ) : (
          <AnswerMarkdown>{message.content}</AnswerMarkdown>
        )}

        {/* Chart */}
        {message.chartData && <ChartRenderer chartData={message.chartData} />}

        {/* Confidence */}
        {typeof confidence === 'number' && (
          <div className="conf">
            <span style={{ fontWeight: 700 }}>Confidence</span>
            <span className="bar">
              <i
                style={{
                  width: `${Math.round(confidence * 100)}%`,
                  background: confidenceColor(confidence),
                }}
              />
            </span>
            <b className="mono">{Math.round(confidence * 100)}%</b>
            {demo?.confidenceNote ? (
              <span className="dim" style={{ fontSize: 11.5 }}>
                — {demo.confidenceNote}
              </span>
            ) : (
              typeof message.processingTime === 'number' && (
                <span className="dim" style={{ fontSize: 11.5 }}>
                  · answered in {(message.processingTime / 1000).toFixed(2)}s
                </span>
              )
            )}
          </div>
        )}

        {/* Evidence metadata */}
        {(message.toolsUsed?.length ||
          (typeof message.processingTime === 'number' &&
            typeof message.confidence !== 'number')) && (
          <div className="cmeta" style={{ marginTop: 4 }}>
            {message.toolsUsed?.map((tool) => (
              <span className="tg tg-b" key={tool}>
                {tool}
              </span>
            ))}
            {typeof message.processingTime === 'number' &&
              typeof message.confidence !== 'number' && (
                <span className="tg tg-n">{(message.processingTime / 1000).toFixed(2)}s</span>
              )}
          </div>
        )}

        {/* Turn actions */}
        <div className="followups">
          {demo?.followups.map((q) => (
            <button className="fu" key={q} onClick={() => onFollowUp?.(q)}>
              {q}
            </button>
          ))}
          <button className="fu" onClick={copy}>
            {copied ? 'Copied' : 'Copy answer'}
          </button>
          {hasSteps && (
            <button className="fu" onClick={() => setShowThinking(!showThinking)}>
              {showThinking ? 'Hide reasoning' : 'Show reasoning'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
